import {
  expenditureReading,
  guidedMissing,
  guidedTargets,
  type BodyStats,
  type ExpenditureReading,
  type GuidedMissing,
  type Program,
} from '@gymcloud/nutrition'

import { useMemo } from 'react'

import { useAuth } from '~/auth/auth-context'
import { todayKey, type WeighIn } from '~/data/weighins'
import { useWeighIns } from '~/data/weighins-store'
import type { Aim, GuidedTargets, NutritionData } from '~/data/nutrition'
import { useNutrition } from '~/data/nutrition-store'
import type { AccountSettings } from '~/data/types'

/**
 * The expenditure and the coached targets, worked out here from what the app already holds —
 * the diary's days, the weigh-ins and the body stats — to the numbers the expenditure spec and
 * the nutrition spec pin. Nothing is asked of the server: a goal's budget follows the slider
 * in the same frame.
 */

export type Expenditure = ExpenditureReading

/** The body stats the estimate starts from, as the account settings carry them. */
export function statsOf(settings: AccountSettings): BodyStats {
  return {
    sex: settings.sex,
    birthDate: settings.birthDate,
    heightM: settings.heightM,
    activityLevel: settings.activityLevel,
  }
}

/**
 * The expenditure as it stands on [today], from the diary's shape and the weigh-ins. The day
 * still being logged — the device's today — is uncounted until it ends; a day asked about in the
 * past was over, and counts as it was logged.
 */
export function expenditureOf(
  nutrition: NutritionData,
  weighIns: readonly WeighIn[],
  settings: AccountSettings,
  today: string,
  inProgress: string | null = todayKey(),
): Expenditure {
  return expenditureReading(
    statsOf(settings),
    nutrition.days.map((day) => ({ day: day.day, calories: day.nutrients.calories })),
    nutrition.incompleteDays,
    weighIns.flatMap((weighIn) =>
      weighIn.weightKg === null ? [] : [{ day: weighIn.day, weightKg: weighIn.weightKg }],
    ),
    today,
    inProgress,
  )
}

/**
 * The targets the coached way arrives at from an expenditure, or what it is missing before it
 * can arrive at any: the body stats, a weight, or — with both — enough for an estimate.
 */
export function coachedTargets(
  expenditure: Expenditure,
  settings: AccountSettings,
  aim: Aim,
  rateKgPerWeek: number | null,
  program: Program,
  targetWeightKg: number | null,
): { targets: GuidedTargets } | { missing: GuidedMissing[] } {
  const weightKg = expenditure.latestWeightKg
  const missing = guidedMissing(statsOf(settings), weightKg)
  if (missing.length > 0 || expenditure.value === null || weightKg === null) {
    return { missing }
  }
  const spent = expenditure.value
  return {
    targets: {
      ...guidedTargets(
        spent,
        aim,
        aim === 'maintain' ? 0 : (rateKgPerWeek ?? 0),
        weightKg,
        program,
        targetWeightKg,
        weightKg,
      ),
      expenditure: spent,
      weightKg,
    },
  }
}

/**
 * The expenditure as it stands today, or null while the diary or the weigh-ins have not
 * arrived — an estimate drawn from half its inputs would be a false one.
 */
export function useExpenditure(): Expenditure | null {
  const nutrition = useNutrition()
  const weighIns = useWeighIns()
  const { settings } = useAuth()
  const today = todayKey()
  // Worked out once per change of what it is worked out from: the filter walks every day
  // since the diary began, and the pages reading it re-render far more often than that.
  return useMemo(
    () =>
      nutrition === null || weighIns === null
        ? null
        : expenditureOf(nutrition, weighIns, settings, today),
    [nutrition, weighIns, settings, today],
  )
}
